import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { AssistantLauncher } from '@foodamigos/ui/dashboard';
import { actionCreators } from '../../store/actions';
import usePlatformAgentState from '../../hooks/context/usePlatformAgentState';
import usePlatformAgentUnread from '../../hooks/platform-agent/usePlatformAgentUnread';
import { PLATFORM_AGENT_NAMES } from '../../services/platform-agent/platformAgent';
import amigoFace from '../../resources/assets/images/platform-agent/amigo-face.png';

/**
 * Ask Amigo (a partner's dashboard: Assistant), bottom right on every page. With news while the panel was closed, a
 * count of the answers the person hasn't seen, in its name too, and a rising count sends the beam round once
 * (usePlatformAgentUnread). Muted, with the reason in its tooltip, while the agent can't be reached; AssistantLauncher
 * then ignores clicks itself. Amigo's face is its image icon (28 px, round); a partner's assistant keeps the Sparkles.
 */
export default function PlatformAgentButton({ ref, className, ...props }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:PlatformAgent:PlatformAgentButton' });
  const dispatch = useDispatch();
  const { unavailable, name } = usePlatformAgentState();
  const { unread, attention } = usePlatformAgentUnread();
  // "Ask Amigo, 2 new" in the dashboard's language.
  const labels = useMemo(() => ({ unread: (_, count) => t(`label_unread.${name}`, { count }) }), [t, name]);
  const face = name === PLATFORM_AGENT_NAMES.Amigo;

  return (
    <AssistantLauncher
      ref={ref}
      {...props}
      // Amigo's face sits 8 px from the edge in the design (Design QA); AssistantLauncher's own is 10 px (fa-pl-2.5),
      // and its tailwind-merge lets this one win.
      className={classnames(className, face && !unavailable && 'fa-pl-2')}
      label={t(`label.${name}`)}
      iconSrc={face ? amigoFace : undefined}
      labels={labels}
      unread={unread}
      attention={attention}
      unavailable={unavailable}
      unavailableLabel={t(`unavailable_label.${name}`)}
      unavailableText={t(`unavailable_text.${name}`)}
      onClick={() => dispatch(actionCreators.platformAgent.open())}
    />
  );
}
